import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Download, GitCompare, Send, Trash2, Undo2, X } from "lucide-react";
import { api, formatNum } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { saveBlob, AY_ADLARI } from "./bankUtils";
import { BankCompareModal } from "./BankCompareModal";

export const BankHistoryModal = ({ onClose }) => {
  const { isAdmin } = useAuth();
  const [rows, setRows] = useState(null);
  const [cmp, setCmp] = useState(null);
  const load = () => api.get("/payroll/bank-history").then((r) => setRows(r.data.rows)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);

  const redownload = async (h) => {
    try {
      const r = await api.get(`/payroll/bank-history/${h.id}/download`, { responseType: "blob" });
      saveBlob(r, h.filename);
      toast.success("Dosya aynen yeniden indirildi");
    } catch { toast.error("İndirilemedi"); }
  };
  const markSent = async (h, sent) => {
    if (!window.confirm(sent ? `${h.filename} bankaya gönderildi olarak işaretlensin mi?` : "Gönderildi işareti kaldırılsın mı?")) return;
    try { await api.put(`/payroll/bank-history/${h.id}/sent`, { sent }); toast.success(sent ? "Gönderildi olarak işaretlendi" : "İşaret kaldırıldı"); load(); }
    catch { toast.error("Güncellenemedi"); }
  };
  const remove = async (h) => {
    if (!window.confirm(`${h.filename} geçmişten silinsin mi?`)) return;
    try { await api.delete(`/payroll/bank-history/${h.id}`); toast.success("Silindi"); load(); }
    catch { toast.error("Silinemedi"); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4" data-testid="bank-history-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col">
        <div className="p-3 border-b flex items-center justify-between">
          <div>
            <div className="text-sm font-bold text-slate-900">Talimat Geçmişi</div>
            <div className="text-[11px] text-slate-500">İndirilen her dosya o anki hâliyle saklanır; yeniden indirince birebir aynısı gelir.</div>
          </div>
          <button onClick={onClose} data-testid="bank-history-close" className="w-7 h-7 rounded-lg hover:bg-slate-100 grid place-items-center"><X size={14} /></button>
        </div>
        <div className="p-3 overflow-y-auto">
          <table className="w-full text-xs">
            <thead className="bg-slate-50">
              <tr>
                <th className="text-left px-2 py-1.5">Oluşturma</th><th className="text-left px-2 py-1.5">Dönem</th>
                <th className="text-left px-2 py-1.5">Tür</th><th className="text-left px-2 py-1.5">Durum</th><th className="text-right px-2 py-1.5">Ödeme</th>
                <th className="text-right px-2 py-1.5">Toplam</th><th className="text-left px-2 py-1.5">Oluşturan</th><th></th>
              </tr>
            </thead>
            <tbody>
              {rows && rows.length === 0 && <tr><td colSpan={8} className="text-center text-slate-400 py-6">Henüz talimat dosyası indirilmemiş</td></tr>}
              {(rows || []).map((h) => (
                <tr key={h.id} className={`border-b border-slate-100 ${h.sent ? "bg-emerald-50/60" : ""}`} data-testid={`bank-history-row-${h.id}`}>
                  <td className="px-2 py-1.5 font-mono text-[11px]">{new Date(h.created_at).toLocaleString("tr-TR")}</td>
                  <td className="px-2 py-1.5 font-semibold">{AY_ADLARI[h.ay - 1]} {h.yil}</td>
                  <td className="px-2 py-1.5">
                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${h.kind === "maas" ? "bg-sky-100 text-sky-800" : "bg-emerald-100 text-emerald-800"}`}>{h.kind === "maas" ? "MAAŞ" : "PRİM"}</span>
                  </td>
                  <td className="px-2 py-1.5" data-testid={`bank-history-status-${h.id}`}>
                    {h.sent ? (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-emerald-600 text-white text-[10px] font-bold" title={`${new Date(h.sent_at).toLocaleString("tr-TR")} · ${h.sent_by_name}`}>
                        <CheckCircle2 size={11} /> GÖNDERİLDİ
                      </span>
                    ) : <span className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 text-[10px] font-bold">TASLAK</span>}
                  </td>
                  <td className="px-2 py-1.5 text-right">{h.count}{h.missing_count ? <span className="text-rose-600"> (+{h.missing_count} IBAN'sız)</span> : null}{h.excluded_count ? <span className="text-slate-500"> (−{h.excluded_count} hariç)</span> : null}</td>
                  <td className="px-2 py-1.5 text-right font-mono-tab font-semibold">{formatNum(h.total)}</td>
                  <td className="px-2 py-1.5 text-slate-600">{h.created_by_name}</td>
                  <td className="px-2 py-1.5 text-right whitespace-nowrap">
                    {h.has_previous && (
                      <button onClick={() => setCmp(h)} data-testid={`bank-history-compare-${h.id}`} title="Aynı ay ve türdeki bir önceki dosyayla karşılaştır" className="mr-1 p-1.5 bg-amber-100 hover:bg-amber-200 text-amber-800 rounded"><GitCompare size={13} /></button>
                    )}
                    <button onClick={() => redownload(h)} data-testid={`bank-history-download-${h.id}`} title="Yeniden indir" className="p-1.5 bg-sky-100 hover:bg-sky-200 text-sky-700 rounded"><Download size={13} /></button>
                    {isAdmin && (
                      <button onClick={() => markSent(h, !h.sent)} data-testid={`bank-history-sent-${h.id}`}
                        title={h.sent ? "Gönderildi işaretini kaldır" : "Bankaya gönderildi olarak işaretle"}
                        className={`ml-1 p-1.5 rounded ${h.sent ? "bg-slate-100 hover:bg-slate-200 text-slate-600" : "bg-emerald-100 hover:bg-emerald-200 text-emerald-700"}`}>
                        {h.sent ? <Undo2 size={13} /> : <Send size={13} />}
                      </button>
                    )}
                    {isAdmin && !h.sent && (
                      <button onClick={() => remove(h)} data-testid={`bank-history-delete-${h.id}`} title="Sil" className="ml-1 p-1.5 bg-rose-100 hover:bg-rose-200 text-rose-700 rounded"><Trash2 size={13} /></button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {cmp && <BankCompareModal hist={cmp} onClose={() => setCmp(null)} />}
    </div>
  );
};
